import React, {
  useState,
  useEffect,
  useRef,
} from "react";
import CloseIcon from '@mui/icons-material/Close';
import { Theme, Dialog, DialogTitle, IconButton, List, ListItem, ListItemText } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTranslation } from "react-i18next";
import { useTaskQueue } from "./hooks";
import { TaskStatus } from "./providers";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    consoleDialogHeader: {
      display: "flex",
      alignItems: "center"
    },
    consoleDialogTitle: {
      userSelect: "none",
      flex: 1,
      paddingBottom: "2px",
      paddingTop: "2px"
    },
    consoleDialogClose: {
      color: theme.palette.grey[500],
      paddingBottom: "2px",
      paddingTop: "2px"
    },
    consoleHeight: {
      background: "black",
      height: "min(50vh, 300px)",
      color: "white",
      overflowY: "scroll"
    },
    consoleLine: {
      padding: theme.spacing(0, 1),
      wordBreak: "break-all"
    }
  })
);

export const TaskQueue = () => {
  /**
   * NOTE: this will be selectable from a seperate ui later, but now it
   * gets automatically set to the current running task. Move this to some other file
   * when the final ui is made.
   **/

  const {taskQueue} = useTaskQueue();
  const runningTask = taskQueue
    .find(task => task.status === TaskStatus.Running);

  return (
    <ConsoleWindow selectedTaskID={runningTask?.id}/>
  );
};

interface ConsoleWindowProps {
  selectedTaskID: string;
}

const ConsoleWindow = (props: ConsoleWindowProps) => {
  const {selectedTaskID} = props;

  const classes = useStyles();
  const listRef = useRef(null);
  const {t} = useTranslation();

  const [lineData, setLineData] = useState([]);
  const {taskQueue, stopRunningTask} = useTaskQueue();

  const textUpdateMilliseconds = 100;
  const activeTask = taskQueue
    .find(task => task.id === selectedTaskID);

  // NOTE: when this is selecteable from an ui, the close button wont actually
  // kill the process, it will only set the selectedTaskID to null
  const onCancel = () => {
    stopRunningTask(selectedTaskID);
  };

  useEffect(() => {
    if (!activeTask) return;

    // Initial exe logs
    setLineData(activeTask.exe.logText);

    const interval = setInterval(() => {
      // Check for new data if the exe is still running
      if (!activeTask || !activeTask.exe.running) return;
      setLineData([...activeTask.exe.logText]);
    }, textUpdateMilliseconds);

    return () => {
      clearInterval(interval);
    };
  }, [activeTask]);

  useEffect(() => {
    if (!listRef.current) return;

    // Auto-scroll to bottom when new data is read
    const height = listRef.current.scrollHeight;
    listRef.current.scrollTop = height;
  }, [listRef, lineData]);

  return (
    <Dialog
      open={!!activeTask}
      fullWidth
      maxWidth={"sm"}
    >
      <div className={classes.consoleDialogHeader}>
        <DialogTitle className={classes.consoleDialogTitle}>
          {t('console.output_logs')}
        </DialogTitle>

        {/* Close button */}
        <IconButton
          aria-label="close"
          className={classes.consoleDialogClose}
          onClick={onCancel}
          size="large">
          <CloseIcon fontSize="small"/>
        </IconButton>
      </div>

      <div>
        <div ref={listRef} className={classes.consoleHeight}>
          <List dense>
            {lineData.map((line, index) =>
              <ListItem className={classes.consoleLine} key={index}>
                <ListItemText>
                  {line}
                </ListItemText>
              </ListItem>)
            }
          </List>
        </div>
      </div>

    </Dialog>
  );
};
